import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { V2Button as Button } from '@abyss/web/ui/Button';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Text } from '@abyss/web/ui/Text';
import { t } from 'i18next';
import { Fragment, useState } from 'react';

import { useNotAvailableUnknownText } from '../../../../hooks/useNotAvailableUnknownText';
import { StoreKeys } from '../../../../hooks/useStore/state';
import { useStore } from '../../../../hooks/useStore/useStore';
import { FacilityLocationsResponse } from '../../../../models/FacilityDetails';
import { ProviderLocation } from '../../../../models/ProviderDetails';
import { mobileOnly, phoneOnly } from '../../../ConstantsStyles';
import { LinkWithTracking } from '../../../LinkWithTracking/LinkWithTracking';
import { IconAlignmentContainer } from '../ProviderLocation.style';
import { infoContainer } from '../ProviderLocationsDetails.style';

type Props = {
  selectedLocation: ProviderLocation | FacilityLocationsResponse;
  isFromVirtualProviderGroup?: boolean;
};

export const EmailAndWebsiteInfo = ({
  selectedLocation,
  isFromVirtualProviderGroup = false,
}: Props) => {
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const phoneScreen = useMediaQuery(phoneOnly) || isWidget;
  const isMobile = useMediaQuery(mobileOnly) || isWidget;
  const [showAdditionalEmails, setShowAdditionalEmails] = useState(false);
  const [showAdditionalWebsites, setShowAdditionalWebsites] = useState(false);
  let contentWidth = '';
  if (isFromVirtualProviderGroup) {
    if (isMobile) {
      contentWidth = '100%';
    } else {
      contentWidth = '50%';
    }
  }

  const {
    isNotAvailableUnknownTextEnabled,
    providerLocations: notAvailableUnknownText,
  } = useNotAvailableUnknownText();
  const fontSize = isNotAvailableUnknownTextEnabled ? '16px' : '18px';
  const primaryEmail = selectedLocation.emailAddress?.[0] || '';
  const additionalEmails = selectedLocation.emailAddress?.slice(1) || [];
  const primaryWebsite =
    selectedLocation.websiteList?.[0] || selectedLocation.website || '';
  const additionalWebsites = selectedLocation?.websiteList?.slice(1) || [];

  return (
    <Fragment>
      <Flex
        css={{
          flexBasis: '100%',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'normal' : 'space-between',
          gap: '12px',
          padding: '$sm calc((100% - 1200px) / 2)',
        }}
        data-auto-testid="email-and-website-info"
        data-testid="email-and-website-info"
      >
        <IconAlignmentContainer
          style={{
            width: contentWidth,
            alignItems: 'flex-start',
          }}
        >
          <IconMaterial
            color="$primary1"
            css={{
              marginTop: '4px',
              'abyss-icon-material': {
                cursor: 'pointer',
                minWidth: '20px',
                marginTop: '4px',
              },
            }}
            icon="web"
            isScreenReadable
            size={20}
            title="Website"
          />
          <Text
            css={{ ...infoContainer(phoneScreen), fontSize }}
            data-auto-testid="location-details-website-header"
            data-testid="location-details-website-header"
            fontWeight="$bold"
          >
            {additionalWebsites.length > 0 ? t('Websites') : t('Website')}
            {primaryWebsite ? (
              <Flex
                css={{
                  ...(additionalWebsites.length > 0 &&
                    showAdditionalWebsites && { gap: '$sm' }),
                }}
                direction="column"
              >
                <LinkWithTracking
                  analyticsInfo={{
                    name: 'website',
                    location: 'body:provider-website',
                  }}
                  css={{
                    ...infoContainer(phoneScreen, true),
                    fontSize,
                  }}
                  data-auto-testid={`primary-website-${primaryWebsite}`}
                  data-testid={`primary-website-${primaryWebsite}`}
                  fontWeight="$bold"
                  href={primaryWebsite}
                  openNewWindow={true}
                >
                  {primaryWebsite}
                </LinkWithTracking>
                {additionalWebsites.length > 0 &&
                  (!showAdditionalWebsites ? (
                    <Button
                      css={{
                        alignSelf: 'flex-start',
                        paddingLeft: 0,
                        paddingTop: 0,
                      }}
                      data-auto-testid="show-additional-websites-button"
                      data-testid="show-additional-websites-button"
                      onClick={() => setShowAdditionalWebsites(true)}
                      variant="text"
                    >
                      {t('countMore', { count: additionalWebsites.length })}
                    </Button>
                  ) : (
                    <Flex css={{ gap: '$xs' }} direction="column">
                      {additionalWebsites.map((site) => (
                        <div key={`${site}-website`}>
                          <LinkWithTracking
                            analyticsInfo={{
                              name: 'website',
                              location: 'body:provider-website',
                            }}
                            css={{
                              ...infoContainer(phoneScreen, true),
                              fontSize,
                            }}
                            data-auto-testid={`${site}-website`}
                            data-testid={`${site}-website`}
                            fontWeight="$bold"
                            href={site}
                            openNewWindow={true}
                          >
                            {site}
                          </LinkWithTracking>
                        </div>
                      ))}
                      <Button
                        css={{
                          alignSelf: 'flex-start',
                          paddingLeft: 0,
                          paddingTop: 0,
                        }}
                        data-auto-testid="hide-additional-websites-button"
                        data-testid="hide-additional-websites-button"
                        onClick={() => setShowAdditionalWebsites(false)}
                        variant="text"
                      >
                        {t('VIEW_LESS')}
                      </Button>
                    </Flex>
                  ))}
              </Flex>
            ) : (
              <Text
                css={{
                  ...infoContainer(phoneScreen),
                }}
                data-auto-testid="provider-website-not-available"
                data-testid="provider-website-not-available"
                fontWeight="$normal"
              >
                {notAvailableUnknownText}
              </Text>
            )}
          </Text>
        </IconAlignmentContainer>

        <IconAlignmentContainer>
          <IconMaterial
            color="$primary1"
            css={{
              marginTop: '5px',
              ...(isMobile && { marginTop: '3px' }),
            }}
            icon="mail"
            size={16.67}
          />
          <Text
            css={{ ...infoContainer(phoneScreen), fontSize }}
            fontWeight="$bold"
          >
            {t('Email')}
            {primaryEmail ? (
              <Flex
                css={{
                  ...(additionalEmails.length > 0 &&
                    showAdditionalEmails && { gap: '$sm' }),
                }}
                direction="column"
              >
                <LinkWithTracking
                  analyticsInfo={{
                    name: 'email-address',
                    location: 'body:provider-email-address',
                    type: 'external',
                  }}
                  css={{ ...infoContainer(phoneScreen, true), fontSize }}
                  data-auto-testid={`primary-email-address-${primaryEmail}`}
                  data-testid={`primary-email-address-${primaryEmail}`}
                  fontWeight="$bold"
                  href={`mailto:${primaryEmail}`}
                  openNewWindow={true}
                >
                  {primaryEmail}
                </LinkWithTracking>
                {additionalEmails.length > 0 &&
                  (!showAdditionalEmails ? (
                    <Button
                      css={{
                        alignSelf: 'flex-start',
                        paddingLeft: 0,
                        paddingTop: 0,
                      }}
                      data-auto-testid="show-additional-emails-button"
                      data-testid="show-additional-emails-button"
                      onClick={() => setShowAdditionalEmails(true)}
                      variant="text"
                    >
                      {t('countMore', { count: additionalEmails.length })}
                    </Button>
                  ) : (
                    <Flex css={{ gap: '$xs' }} direction="column">
                      {additionalEmails.map((email) => (
                        <div key={`${email}-email-address`}>
                          <LinkWithTracking
                            analyticsInfo={{
                              name: 'email-address',
                              location: 'body:provider-email-address',
                              type: 'external',
                            }}
                            css={{
                              ...infoContainer(phoneScreen, true),
                              fontSize,
                            }}
                            data-auto-testid={`${email}-email-address`}
                            data-testid={`${email}-email-address`}
                            fontWeight="$bold"
                            href={`mailto:${email}`}
                            openNewWindow={true}
                          >
                            {email}
                          </LinkWithTracking>
                        </div>
                      ))}
                      <Button
                        css={{
                          alignSelf: 'flex-start',
                          paddingLeft: 0,
                          paddingTop: 0,
                        }}
                        data-auto-testid="hide-additional-emails-button"
                        data-testid="hide-additional-emails-button"
                        onClick={() => setShowAdditionalEmails(false)}
                        variant="text"
                      >
                        {t('VIEW_LESS')}
                      </Button>
                    </Flex>
                  ))}
              </Flex>
            ) : (
              <Text
                css={{
                  ...infoContainer(phoneScreen),
                }}
                data-auto-testid="provider-email-not-available"
                data-testid="provider-email-not-available"
                fontWeight="$normal"
              >
                {notAvailableUnknownText}
              </Text>
            )}
          </Text>
        </IconAlignmentContainer>
      </Flex>
    </Fragment>
  );
};
